import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Bell, BellOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EmailAutomationToggles — drop-in pill(s) listing every scheduled email
 * automation that belongs to the given `category`. Owner can flip each
 * one on/off; the backend scheduler checks the flag at fire time.
 *
 * Props:
 *   category — one of 'quick_reminders' | 'monthly_bills' | 'tradelines'
 *   compact  — if true, render a single-line row (used on top-of-page bars)
 */
export default function EmailAutomationToggles({ category, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // {key: bool}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/email-automations`, { timeout: 10000 });
      setItems((data.items || []).filter((i) => !category || i.category === category));
    } catch (e) {
      console.error('[EmailAutomationToggles] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const flip = async (key, next) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/email-automations/${key}`,
        { enabled: next },
        { timeout: 10000 },
      );
      setItems((prev) => prev.map((i) => (i.key === key ? data.automation : i)));
    } catch (e) {
      console.error('[EmailAutomationToggles] flip failed', e);
      alert('Failed to update automation. Please try again.');
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="email-automation-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading automations…
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div
      className={`space-y-2 ${compact ? '' : 'p-4 rounded-xl border border-white/10 bg-slate-900/50'}`}
      data-testid={`email-automations-${category || 'all'}`}
    >
      {!compact && (
        <div className="text-xs uppercase tracking-wider text-teal-300 font-semibold mb-3 flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5" /> Automated Email Schedules
        </div>
      )}
      {items.map((it) => (
        <div
          key={it.key}
          className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/60 border border-white/5 hover:border-teal-400/30 transition"
          data-testid={`email-automation-${it.key}`}
        >
          <button
            type="button"
            onClick={() => flip(it.key, !it.enabled)}
            disabled={!!saving[it.key]}
            className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition ${
              it.enabled ? 'bg-emerald-500' : 'bg-slate-600'
            } ${saving[it.key] ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            data-testid={`email-automation-toggle-${it.key}`}
            aria-label={`${it.enabled ? 'Disable' : 'Enable'} ${it.label}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                it.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-white truncate">{it.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                  it.enabled
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-slate-600/40 text-slate-400 border border-slate-500/30'
                }`}
              >
                {it.enabled ? 'ON' : 'OFF'}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              📅 {it.schedule}
            </div>
            {!compact && (
              <div className="text-[12px] text-slate-300/80 mt-1.5 leading-snug">
                {it.description}
              </div>
            )}
          </div>
          {!it.enabled && (
            <BellOff className="w-4 h-4 text-slate-500 shrink-0 mt-1" />
          )}
        </div>
      ))}
    </div>
  );
}
